import {
  useAppConfiguration,
  type TriggerType,
} from "@/api/app-configuration.api";
import { triggers } from "@/data/triggers";
import { NewCampaign } from "@/types/campaign";
import { Link, LinkOptions } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { Check, Circle } from "lucide-react";

type Props = {
  triggers?: NewCampaign["triggers"];
  buildTriggerLink: (triggerId: string) => LinkOptions;
};

export function TriggerCards({ triggers, buildTriggerLink }: Props) {
  const { data: appConfiguration } = useAppConfiguration();

  return (
    <ul className="grid w-full grid-cols-[repeat(auto-fit,_minmax(auto,_300px))] justify-center gap-4">
      {appConfiguration?.triggerTypes.map((trigger) => (
        <TriggerCard
          key={trigger.event}
          id={trigger.event}
          trigger={trigger}
          enabled={triggers?.some((t) => t.event === trigger.event) || false}
          buildLink={buildTriggerLink}
        />
      ))}
    </ul>
  );
}

function TriggerCard({
  id,
  trigger,
  enabled,
  buildLink,
}: {
  id: string;
  trigger: TriggerType;
  enabled: boolean;
  buildLink: (triggerId: string) => LinkOptions;
}) {
  const Icon = triggers[id as keyof typeof triggers]?.icon || Circle;

  return (
    <li className="max-w-75">
      <Link
        {...buildLink(id)}
        className="group border-theme-outline-variant relative flex overflow-hidden rounded-md border"
      >
        <motion.div
          className="group-hover:bg-theme-primary border-theme-outline-variant text-theme-on-background group-hover:text-theme-on-primary group-hover:border-theme-primary flex aspect-square w-[70px] shrink-0 items-center justify-center border-r transition-colors"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          {enabled ? (
            <Check className="size-8 text-green-600 transition-colors group-hover:text-white dark:text-green-400" />
          ) : (
            <Icon className="size-5" />
          )}
        </motion.div>
        <motion.div
          className="flex items-center overflow-hidden"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{
            duration: 0.2,
          }}
        >
          <span className="text-theme-on-background truncate p-3 text-[11px] font-semibold transition-all group-hover:translate-x-1">
            {trigger.label}
          </span>
        </motion.div>
      </Link>
    </li>
  );
}
